import React, { useEffect, useState } from "react";
import { Activity, FileDown, RefreshCw, Package2, Loader2, Sparkles } from "lucide-react";
import { Tile, Badge } from "./Primitives";
import { getIngestFeed } from "../lib/s3Api";
import { formatNumber, cn } from "../lib/utils";

// -----------------------------------------------------------------------------
// Ingest Watch Feed · Live Data Pipeline card
// -----------------------------------------------------------------------------
// Lights up for every new file 1Rotary drops into the bucket since the last
// cycle. Pre-existing files (pre-handshake) are seeded silently on the
// backend so the feed stays clean. Each event is tagged `new_file` or
// `updated_file`. The IT Head can trigger an on-demand refresh.
// -----------------------------------------------------------------------------
const AUTO_REFRESH_MS = 60_000;

export default function IngestWatchFeed({ connectionId, className }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  async function load() {
    setError(null);
    try {
      const r = await getIngestFeed({ connectionId, limit: 25 });
      setEvents(r.events || []);
      setLastFetch(new Date());
    } catch (e) {
      setError(e.message || "could not load feed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [connectionId]); // eslint-disable-line

  const newCount = events.filter(e => e.event_type === "new_file").length;
  const updCount = events.filter(e => e.event_type === "updated_file").length;

  return (
    <Tile className={cn("col-span-12", className)}
          testid="ingest-watch-feed"
          title="1Rotary Ingest Watch · new & updated files"
          action={
            <div className="flex items-center gap-2 flex-wrap">
              <Badge status={newCount > 0 ? "success" : "info"} testid="ingest-feed-new-count">
                <Sparkles size={10} className="inline mr-1" />
                {newCount} new
              </Badge>
              <Badge status={updCount > 0 ? "warning" : "info"} testid="ingest-feed-updated-count">
                <RefreshCw size={10} className="inline mr-1" />
                {updCount} updated
              </Badge>
              <button data-testid="ingest-feed-refresh"
                      onClick={() => { setRefreshing(true); load(); }}
                      disabled={refreshing || loading}
                      className="px-2.5 py-1.5 text-[11px] border border-border rounded-sm hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-40">
                {refreshing || loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                Refresh
              </button>
            </div>
          }>
      {loading ? (
        <div className="py-6 text-center text-[11.5px] text-muted"
             data-testid="ingest-feed-loading">
          <Loader2 size={14} className="animate-spin inline mr-2" />
          Loading ingest feed…
        </div>
      ) : error ? (
        <div className="py-4 text-center text-[11.5px] text-critical"
             data-testid="ingest-feed-error">{error}</div>
      ) : events.length === 0 ? (
        <div className="py-6 text-center" data-testid="ingest-feed-empty">
          <Activity size={22} className="mx-auto mb-2 text-muted opacity-50" />
          <div className="text-[12px] text-fg font-medium">No new drops yet</div>
          <div className="text-[11px] text-muted mt-1 max-w-md mx-auto leading-relaxed">
            The feed stays empty until 1Rotary writes a new file (or updates an
            existing one) in the pilot bucket. Pre-existing files are tracked
            silently so this list only shows actual deltas.
          </div>
          {lastFetch && (
            <div className="text-[10px] text-muted mt-2">
              last checked {relative(lastFetch)}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]" data-testid="ingest-feed-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                  <th className="text-left px-2 py-2 w-24">Event</th>
                  <th className="text-left px-2 py-2">S3 key</th>
                  <th className="text-right px-2 py-2 w-20">Size</th>
                  <th className="text-left px-2 py-2 w-36">When</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id || `${e.key}-${e.ts}`}
                      data-testid={`ingest-feed-row-${e.id || e.key}`}
                      className="border-b border-border/50 hover:bg-surface-2/40">
                    <td className="px-2 py-2 align-top">
                      {e.event_type === "new_file" ? (
                        <Badge status="success">
                          <Sparkles size={9} className="inline mr-0.5" />new
                        </Badge>
                      ) : (
                        <Badge status="warning">
                          <RefreshCw size={9} className="inline mr-0.5" />updated
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <code className="font-mono-tabular text-[11px] break-all">{e.key}</code>
                    </td>
                    <td className="px-2 py-2 align-top text-right font-mono-tabular text-fg/90">
                      <Package2 size={9} className="inline mr-0.5 text-muted" />
                      {formatSize(e.size)}
                    </td>
                    <td className="px-2 py-2 align-top text-muted">
                      {relative(e.ts)}
                      <div className="text-[10px] text-muted/70">
                        S3 LM: {(e.last_modified || "").slice(0, 19).replace("T", " ")}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lastFetch && (
            <div className="text-[10px] text-muted mt-2 text-right">
              auto-refreshes every {AUTO_REFRESH_MS / 1000}s · last at {relative(lastFetch)}
            </div>
          )}
        </>
      )}
    </Tile>
  );
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
}
function relative(iso) {
  if (!iso) return "";
  const t = typeof iso === "string" ? Date.parse(iso) : iso instanceof Date ? iso.getTime() : iso;
  if (Number.isNaN(t)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
